import { useCallback, useState } from 'react'
import { createAutomationGroup } from '@/api'
import { uploadReferentialGridToS3 } from '../api/uploadReferentialGridToS3'
import { buildRangesFromGridData, createEmptyGridData } from '../utils/gridData'
import type { CreateReferentialSource } from '../types'

export type CreateReferentialInput = {
  name: string
  description?: string
  groupId: string
  userId: string
  configurationId: string
  clientOrganizationId?: string
  active: boolean
  source: CreateReferentialSource
  gridData?: string[][]
}

type CreateReferentialResponse = {
  s3UploadUrl?: string
}

export function useCreateReferential() {
  const [isCreating, setIsCreating] = useState(false)

  const createReferential = useCallback(
    async ({
      name,
      description,
      groupId,
      userId,
      configurationId,
      clientOrganizationId,
      active,
      source,
      gridData,
    }: CreateReferentialInput) => {
      const resolvedGridData =
        source === 'empty' ? createEmptyGridData() : (gridData ?? [])

      if (source === 'import' && resolvedGridData.length === 0) {
        throw new Error('IMPORT_REQUIRED')
      }

      const file = new Blob([JSON.stringify(resolvedGridData)], {
        type: 'application/json',
      })

      setIsCreating(true)

      try {
        const response = (await createAutomationGroup({
          name,
          description,
          configurationId,
          groupId,
          userId,
          active,
          clientOrganizationId,
          data: {
            matching: [],
            payload: [],
            ranges: buildRangesFromGridData(resolvedGridData),
          },
          fileSize: file.size,
        })) as CreateReferentialResponse

        const signedUrl = response.s3UploadUrl
        if (!signedUrl) {
          throw new Error('MISSING_UPLOAD_URL')
        }

        await uploadReferentialGridToS3(signedUrl, file)
      } finally {
        setIsCreating(false)
      }
    },
    []
  )

  return {
    createReferential,
    isCreating,
  }
}
